import { enqueueFirebaseIdTokenRevokedTeardown } from '@/libs/api/firebase-cloud-functions-scope';
import type { ApiError, TokenProvider } from '@/libs/api/types';
import { getCurrentEnvUrls } from '@/libs/config/env-urls';
import { isAxiosTimeoutError, showRequestTimeoutAlert } from '@/libs/errors/request-timeout';
import { captureException } from '@/libs/logger';
import { ensureOnline, isAxiosNetworkError, showOfflineAlert } from '@/libs/network';
import { useUiStore } from '@/stores';
import axios, { AxiosError, CanceledError, type InternalAxiosRequestConfig } from 'axios';

export const ID_TOKEN_REVOKED = 'auth/id-token-revoked';

function readApiResponseBodyCode(responseData: unknown): string | undefined {
  if (!responseData || typeof responseData !== 'object') {
    return undefined;
  }
  const obj = responseData as Record<string, unknown>;
  const flat = obj.code;
  if (typeof flat === 'string') {
    return flat;
  }
  const nested = obj.error;
  if (
    nested &&
    typeof nested === 'object' &&
    'code' in nested &&
    typeof (nested as { code?: unknown }).code === 'string'
  ) {
    return (nested as { code: string }).code;
  }
  return undefined;
}

/**
 * Resolves `baseURL` per-request from the env store, so toggling Firebase env
 * at runtime swaps the cloud-functions backend without rebuilding the axios
 * instance.
 */
export function createCloudFunctionsBaseUrlInterceptor() {
  return {
    onFulfilled: (config: InternalAxiosRequestConfig) => {
      config.baseURL = getCurrentEnvUrls().firebaseCloudFunctionUrl;
      return config;
    },
  };
}

export function createNetworkCheckInterceptor() {
  return {
    onFulfilled: async (config: InternalAxiosRequestConfig) => {
      await ensureOnline();
      return config;
    },
  };
}

export function createAuthInterceptor(tokenProvider: TokenProvider) {
  return {
    onFulfilled: async (config: InternalAxiosRequestConfig) => {
      if ((config as InternalAxiosRequestConfig & { skipAuth?: boolean }).skipAuth) {
        return config;
      }

      const token = await tokenProvider.getAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
  };
}

export function createErrorInterceptor() {
  return {
    onRejected: (error: unknown) => {
      if (error instanceof CanceledError) {
        return Promise.reject(error);
      }

      if (!axios.isAxiosError(error)) {
        return Promise.reject(error);
      }

      const axiosError = error as AxiosError<ApiError>;

      const serverCode = readApiResponseBodyCode(axiosError.response?.data);
      const status = axiosError.response?.status || 0;

      const apiError: ApiError & { alertHandled?: boolean } = {
        message:
          (axiosError.response?.data && typeof axiosError.response.data === 'object'
            ? (axiosError.response.data as { message?: string }).message
            : undefined) ||
          axiosError.message ||
          'Unknown error',
        status,
        code: axiosError.code,
        serverCode,
        errors: axiosError.response?.data?.errors,
        request: axiosError.request,
      };

      if (status === 401 && serverCode === ID_TOKEN_REVOKED) {
        enqueueFirebaseIdTokenRevokedTeardown().catch(() => {});
        return Promise.reject(apiError);
      }

      if (isAxiosTimeoutError(axiosError)) {
        useUiStore.getState().hideGlobalLoading();
        showRequestTimeoutAlert();
        apiError.alertHandled = true;
        captureException({ error_payload: apiError }, { tags: { usk_log_type: 'api_timeout' } });
        return Promise.reject(apiError);
      }

      if (isAxiosNetworkError(axiosError)) {
        useUiStore.getState().hideGlobalLoading();
        showOfflineAlert();
        apiError.alertHandled = true;
        captureException({ error_payload: apiError }, { tags: { usk_log_type: 'api_offline' } });
        return Promise.reject(apiError);
      }

      captureException(
        {
          error_payload: apiError,
        },
        { tags: { usk_log_type: 'api_error' } },
      );

      return Promise.reject(apiError);
    },
  };
}
